import { useEffect, useState } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function monthLabel(yyyyMM) {
  const [y, m] = yyyyMM.split('-');
  return `${MONTH_LABELS[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

function pct(a, b) {
  if (!b) return null;
  return Math.round((a / b) * 100);
}

function PctBadge({ value }) {
  if (value === null) return <span className="text-gray-300">—</span>;
  const cls = value >= 90 ? 'bg-emerald-100 text-emerald-700'
    : value >= 70 ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${cls}`}>{value}%</span>;
}

function Sparkline({ pm, months }) {
  const max = Math.max(1, ...months.map(m => (pm.months[m]?.total || 0)));
  return (
    <svg viewBox={`0 0 ${months.length * 6} 16`} className="w-16 h-4" preserveAspectRatio="none">
      {months.map((m, i) => {
        const d = pm.months[m] || { total: 0, on_time: 0 };
        const h = max ? Math.max(1, Math.round((d.total / max) * 14)) : 1;
        const onH = max ? Math.round((d.on_time / max) * 14) : 0;
        return (
          <g key={m}>
            <rect x={i * 6} y={16 - h} width={5} height={h} fill="#E5E7EB" rx="1" />
            <rect x={i * 6} y={16 - onH} width={5} height={onH} fill="#10B981" rx="1" />
          </g>
        );
      })}
    </svg>
  );
}

export default function PmAchievementPage() {
  const { currentUser } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    api.get('pm-achievements').then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6 text-gray-400 text-sm">Loading achievement data…</div>;
  if (!data)   return <div className="p-6 text-red-400 text-sm">Failed to load achievement data.</div>;

  const { months, pms, team_avg } = data;
  const me = pms.find(p => p.is_me);

  const myOnTimePct = me ? pct(me.totals.on_time, me.totals.total) : null;
  const teamTotalOnTime = pms.reduce((s, p) => s + p.totals.on_time, 0);
  const teamTotalDue    = pms.reduce((s, p) => s + p.totals.total, 0);
  const teamOnTimePct   = pct(teamTotalOnTime, teamTotalDue);

  // Chart sizing
  const maxBar = Math.max(1, ...months.map(m => {
    const teamTotal = pms.reduce((s, p) => s + (p.months[m]?.total || 0), 0);
    return teamTotal;
  }));

  const activeMonth = selectedMonth || months[months.length - 1];

  // Milestones for selected month (per PM)
  const monthPms = pms.map(p => ({
    ...p,
    month_data: p.months[activeMonth] || { total: 0, on_time: 0, late: 0, pending: 0 },
  })).filter(p => p.month_data.total > 0).sort((a, b) => b.month_data.on_time - a.month_data.on_time);

  return (
    <div className="fade-in">
      <div className="mb-3">
        <h2 className="text-base font-extrabold text-navy">Milestone Achievement</h2>
        <div className="text-[9px] text-gray-500 mt-0.5">
          On-time delivery per PM · last 12 months · as at {data.generated_at}
          <span className="ml-2 text-gray-400" title="On time = milestone achieved (invoiced or confirmed delivered) by its contract date. Contract date changes via change control are reflected automatically.">ⓘ on time = achieved by contract date</span>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        {me && (
          <>
            <div className="px-3 py-2 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-700">
              <div className="text-lg font-extrabold leading-none">{me.totals.on_time}</div>
              <div className="text-[8px] font-bold mt-0.5">My on-time deliveries</div>
            </div>
            <div className="px-3 py-2 rounded-lg border bg-white border-gray-200 text-gray-700">
              <div className="text-lg font-extrabold leading-none">
                {myOnTimePct !== null ? `${myOnTimePct}%` : '—'}
              </div>
              <div className="text-[8px] font-bold mt-0.5">My on-time rate</div>
            </div>
          </>
        )}
        <div className="px-3 py-2 rounded-lg border bg-white border-gray-200 text-gray-700">
          <div className="text-lg font-extrabold leading-none">
            {teamOnTimePct !== null ? `${teamOnTimePct}%` : '—'}
          </div>
          <div className="text-[8px] font-bold mt-0.5">Team on-time rate</div>
        </div>
        <div className="px-3 py-2 rounded-lg border bg-white border-gray-200 text-gray-700">
          <div className="text-lg font-extrabold leading-none">{pms.length}</div>
          <div className="text-[8px] font-bold mt-0.5">PMs tracked</div>
        </div>
      </div>

      {/* Month-by-month chart */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[10px] font-bold text-navy uppercase">Monthly achievement — last 12 months</h3>
          <div className="flex items-center gap-3 text-[8px]">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-400" />On time</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-orange-400" />Late</span>
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-gray-200" />Pending</span>
            {me && <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />My avg</span>}
          </div>
        </div>

        <div className="flex items-end gap-1" style={{ height: '96px' }}>
          {months.map(m => {
            const teamOnTime  = pms.reduce((s, p) => s + (p.months[m]?.on_time  || 0), 0);
            const teamLate    = pms.reduce((s, p) => s + (p.months[m]?.late     || 0), 0);
            const teamPending = pms.reduce((s, p) => s + (p.months[m]?.pending  || 0), 0);
            const teamTotal   = teamOnTime + teamLate + teamPending;
            const scale       = teamTotal / maxBar;
            const isActive    = m === activeMonth;

            return (
              <button
                key={m}
                type="button"
                onClick={() => setSelectedMonth(m)}
                className={`flex-1 flex flex-col justify-end h-full group ${isActive ? 'opacity-100' : 'opacity-70 hover:opacity-90'}`}
                title={`${monthLabel(m)}: ${teamOnTime} on-time, ${teamLate} late, ${teamPending} pending`}
              >
                <div
                  className={`flex flex-col rounded-t overflow-hidden transition-all ${isActive ? 'ring-1 ring-blue-400 ring-offset-1' : ''}`}
                  style={{ height: `${scale * 88}px`, minHeight: teamTotal ? '4px' : '0' }}
                >
                  {teamOnTime  > 0 && <div className="bg-emerald-400" style={{ height: `${(teamOnTime  / teamTotal) * 100}%` }} />}
                  {teamLate    > 0 && <div className="bg-orange-400"  style={{ height: `${(teamLate    / teamTotal) * 100}%` }} />}
                  {teamPending > 0 && <div className="bg-gray-200"    style={{ height: `${(teamPending / teamTotal) * 100}%` }} />}
                </div>
              </button>
            );
          })}
        </div>

        <div className="flex gap-1 mt-1">
          {months.map((m, i) => (
            <div key={m} className="flex-1 text-center text-[6px] text-gray-400 leading-tight">
              {(i % 2 === 0 || i === months.length - 1) ? monthLabel(m) : ''}
            </div>
          ))}
        </div>
      </div>

      {/* Month breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden mb-3">
        <div className="px-3 py-2 border-b bg-gray-50 flex items-center justify-between">
          <h3 className="text-[10px] font-bold text-navy uppercase">
            {monthLabel(activeMonth)} — per PM
          </h3>
          <div className="flex items-center gap-1">
            {months.map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setSelectedMonth(m)}
                className={`px-1.5 py-0.5 rounded text-[7px] font-bold transition-colors ${
                  m === activeMonth
                    ? 'bg-navy text-white'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {monthLabel(m)}
              </button>
            ))}
          </div>
        </div>

        {monthPms.length === 0 ? (
          <div className="px-3 py-6 text-center text-gray-400 text-[10px]">No milestones due this month</div>
        ) : (
          <div className="overflow-x-auto"><table className="w-full text-[10px]">
            <thead>
              <tr className="border-b bg-gray-50/60 text-[8px] uppercase text-gray-500">
                <th className="px-3 py-1.5 text-left">Project Manager</th>
                <th className="px-2 py-1.5 text-center">Due</th>
                <th className="px-2 py-1.5 text-center">On Time</th>
                <th className="px-2 py-1.5 text-center">Late</th>
                <th className="px-2 py-1.5 text-center">Pending</th>
                <th className="px-2 py-1.5 text-center">Rate</th>
                <th className="px-3 py-1.5 text-left">Progress</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {monthPms.map(p => {
                const d = p.month_data;
                const rate = pct(d.on_time, d.total);
                const barPct = d.total ? Math.round((d.on_time / d.total) * 100) : 0;
                return (
                  <tr key={p.pm_id} className={p.is_me ? 'bg-blue-50/40' : 'hover:bg-gray-50/60'}>
                    <td className="px-3 py-2 font-semibold text-navy">
                      {p.pm_name}
                      {p.is_me && <span className="ml-1 text-[7px] text-blue-500 font-bold uppercase">you</span>}
                    </td>
                    <td className="px-2 py-2 text-center text-gray-600">{d.total}</td>
                    <td className="px-2 py-2 text-center font-bold text-emerald-600">{d.on_time}</td>
                    <td className="px-2 py-2 text-center">
                      {d.late > 0 ? <span className="font-bold text-orange-500">{d.late}</span> : <span className="text-gray-300">0</span>}
                    </td>
                    <td className="px-2 py-2 text-center">
                      {d.pending > 0 ? <span className="text-gray-500">{d.pending}</span> : <span className="text-gray-300">0</span>}
                    </td>
                    <td className="px-2 py-2 text-center"><PctBadge value={rate} /></td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${barPct}%` }} />
                        </div>
                        <span className="text-[8px] text-gray-400 w-6 text-right">{barPct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>

      {/* Leaderboard — 12-month totals */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b bg-gray-50">
          <h3 className="text-[10px] font-bold text-navy uppercase">12-month leaderboard</h3>
          <div className="text-[8px] text-gray-400 mt-0.5">Ranked by on-time deliveries · click a PM to highlight</div>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-[10px]">
          <thead>
            <tr className="border-b bg-gray-50/60 text-[8px] uppercase text-gray-500">
              <th className="px-3 py-1.5 text-left w-6">#</th>
              <th className="px-3 py-1.5 text-left">PM</th>
              <th className="px-2 py-1.5 text-center">Total due</th>
              <th className="px-2 py-1.5 text-center">On time</th>
              <th className="px-2 py-1.5 text-center">Late</th>
              <th className="px-2 py-1.5 text-center">Rate</th>
              <th className="px-3 py-1.5 text-left">12-month trend</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pms.map((p, idx) => {
              const rate = pct(p.totals.on_time, p.totals.total);
              const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
              return (
                <tr key={p.pm_id} className={p.is_me ? 'bg-blue-50/40' : 'hover:bg-gray-50/60'}>
                  <td className="px-3 py-2 text-gray-400 font-semibold">
                    {medal || <span className="text-[9px]">{idx + 1}</span>}
                  </td>
                  <td className="px-3 py-2 font-semibold text-navy">
                    {p.pm_name}
                    {p.is_me && <span className="ml-1 text-[7px] text-blue-500 font-bold uppercase">you</span>}
                  </td>
                  <td className="px-2 py-2 text-center text-gray-600">{p.totals.total}</td>
                  <td className="px-2 py-2 text-center font-bold text-emerald-600">{p.totals.on_time}</td>
                  <td className="px-2 py-2 text-center">
                    {p.totals.late > 0 ? <span className="font-bold text-orange-500">{p.totals.late}</span> : <span className="text-gray-300">0</span>}
                  </td>
                  <td className="px-2 py-2 text-center"><PctBadge value={rate} /></td>
                  <td className="px-3 py-2"><Sparkline pm={p} months={months} /></td>
                </tr>
              );
            })}
            {pms.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">No milestone data for the last 12 months</td></tr>
            )}
          </tbody>
        </table></div>

        {/* Team average footer */}
        {pms.length > 0 && (
          <div className="px-3 py-2 border-t bg-gray-50/60 flex items-center gap-4 text-[9px] text-gray-500">
            <span className="font-bold text-gray-600">Team average</span>
            <span>{teamTotalDue} milestones due</span>
            <span className="text-emerald-600 font-semibold">{teamTotalOnTime} on time</span>
            <PctBadge value={teamOnTimePct} />
          </div>
        )}
      </div>
    </div>
  );
}
